import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileText, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import {
  useQuoteTemplates,
  useUpdateQuoteTemplate,
  useDeleteQuoteTemplate,
  type QuoteTemplateRow,
} from "@/hooks/useQuoteTemplates";
import { SERVICE_TYPE_LABELS, type ServiceType } from "@/types/quotes";
import { draftsToTemplateItems, parseTemplateGuestCount, templateItemsToDrafts } from "@/lib/quoteTemplates";
import { QuoteItemPicker } from "./QuoteItemPicker";
import { SortableLineItemList } from "./SortableLineItemList";
import { useDragEscapeGuard, useLineItemDrafts } from "./useLineItemDrafts";

interface QuoteTemplatesViewProps {
  onUseTemplate: (template: QuoteTemplateRow) => void;
}

export function QuoteTemplatesView({ onUseTemplate }: QuoteTemplatesViewProps) {
  const { data: templates, isLoading, isError, refetch } = useQuoteTemplates();
  const [editing, setEditing] = useState<QuoteTemplateRow | null>(null);
  const [deleting, setDeleting] = useState<QuoteTemplateRow | null>(null);
  const deleteTemplate = useDeleteQuoteTemplate();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <p className="text-sm text-muted-foreground">Couldn't load templates.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!templates?.length) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="rounded-full bg-muted p-4 mb-4">
            <FileText className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-display text-lg font-semibold mb-1">No templates yet</h3>
          <p className="text-muted-foreground text-sm max-w-md">
            Open a quote and choose Save as Template to reuse it here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map((t) => (
          <TemplateCard
            key={t.id}
            template={t}
            onUse={onUseTemplate}
            onEdit={setEditing}
            onDelete={setDeleting}
          />
        ))}
      </div>

      <EditTemplateDialog template={editing} onClose={() => setEditing(null)} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template "{deleting?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Quotes already created from it are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleting) deleteTemplate.mutate(deleting.id);
                setDeleting(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface TemplateCardProps {
  template: QuoteTemplateRow;
  onUse: (t: QuoteTemplateRow) => void;
  onEdit: (t: QuoteTemplateRow) => void;
  onDelete: (t: QuoteTemplateRow) => void;
}

function TemplateCard({ template: t, onUse, onEdit, onDelete }: TemplateCardProps) {
  return (
    <Card>
      <CardContent className="flex h-full flex-col gap-3 p-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium">{t.name}</span>
          </div>
          {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline" className="text-[10px]">
            {SERVICE_TYPE_LABELS[t.service_type as ServiceType] ?? t.service_type}
          </Badge>
          {t.guest_count != null && <span>{t.guest_count} guests</span>}
          <span>{t.items?.length ?? 0} items</span>
        </div>
        {t.notes && <p className="text-xs text-muted-foreground line-clamp-2">{t.notes}</p>}
        <div className="mt-auto flex flex-wrap gap-2 pt-1">
          <Button size="sm" onClick={() => onUse(t)}>
            <Plus className="h-4 w-4 mr-1" />
            Use template
          </Button>
          <Button size="sm" variant="outline" onClick={() => onEdit(t)} aria-label={`Edit ${t.name}`}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(t)}
            aria-label={`Delete ${t.name}`}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EditTemplateDialog({ template, onClose }: { template: QuoteTemplateRow | null; onClose: () => void }) {
  // Held here, not in the form: DialogContent is the one that sees Escape.
  const dragGuard = useDragEscapeGuard();
  return (
    <Dialog open={!!template} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" onEscapeKeyDown={dragGuard.onEscapeKeyDown}>
        <DialogHeader>
          <DialogTitle className="font-display">Edit Template</DialogTitle>
        </DialogHeader>
        {/* Keyed so the fields re-seed from each template that's opened. */}
        {template && (
          <EditTemplateForm
            key={template.id}
            template={template}
            onDone={onClose}
            onDraggingChange={dragGuard.onDraggingChange}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface EditTemplateFormProps {
  template: QuoteTemplateRow;
  onDone: () => void;
  onDraggingChange: (dragging: boolean) => void;
}

function EditTemplateForm({ template, onDone, onDraggingChange }: EditTemplateFormProps) {
  const updateTemplate = useUpdateQuoteTemplate();
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description ?? "");
  const [guestCount, setGuestCount] = useState(template.guest_count != null ? String(template.guest_count) : "");
  const [notes, setNotes] = useState(template.notes ?? "");
  const { lineItems, addPickedItem, addCustomLine, updateLine, removeLine, reorderLines, resizePerPerson } =
    useLineItemDrafts(() => templateItemsToDrafts(template.items));
  const [pickerOpen, setPickerOpen] = useState(false);

  const parsedGuestCount = parseTemplateGuestCount(guestCount);
  const guestCountInvalid = parsedGuestCount === undefined;
  const canSave = !!name.trim() && !guestCountInvalid && !updateTemplate.isPending;

  const handleGuestCountChange = (value: string) => {
    setGuestCount(value);
    // Per-person lines follow the guest count, as in the quote form.
    const n = parseTemplateGuestCount(value);
    if (typeof n === "number") resizePerPerson(n);
  };

  const handleSave = async () => {
    if (!canSave || parsedGuestCount === undefined) return;
    try {
      await updateTemplate.mutateAsync({
        id: template.id,
        name: name.trim(),
        description: description.trim() || null,
        notes: notes.trim() || null,
        guest_count: parsedGuestCount,
        items: draftsToTemplateItems(lineItems),
      });
    } catch {
      // The hook's onError already toasted; keep the dialog open.
      return;
    }
    onDone();
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="template-name" className="text-xs font-medium">Name *</Label>
        <Input id="template-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="template-description" className="text-xs font-medium">Description</Label>
        <Input id="template-description" value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="template-guest-count" className="text-xs font-medium">Guest count</Label>
        <Input
          id="template-guest-count"
          inputMode="numeric"
          value={guestCount}
          onChange={(e) => handleGuestCountChange(e.target.value)}
          placeholder="Leave blank for no preset"
        />
        {guestCountInvalid && <p className="text-xs text-destructive">Guest count must be 1 or more</p>}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="template-notes" className="text-xs font-medium">Notes (shown to client)</Label>
        <Textarea id="template-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        <p className="text-xs text-muted-foreground">
          These notes pre-fill every quote made from this template, so keep one client's details out.
        </p>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium">Line items</Label>
          <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Item
          </Button>
        </div>
        {lineItems.length === 0 ? (
          <p className="rounded-lg border-2 border-dashed py-6 text-center text-sm text-muted-foreground">No items yet</p>
        ) : (
          <SortableLineItemList
            items={lineItems}
            onChange={updateLine}
            onRemove={removeLine}
            onReorder={reorderLines}
            onDraggingChange={onDraggingChange}
          />
        )}
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onDone}>Cancel</Button>
        <Button size="sm" onClick={handleSave} disabled={!canSave}>
          {updateTemplate.isPending ? "Saving..." : "Save"}
        </Button>
      </div>

      {/* Mounted only while open: its catalog queries aren't needed for a rename. */}
      {pickerOpen && (
        <QuoteItemPicker
          open
          onOpenChange={setPickerOpen}
          guestCount={parsedGuestCount ?? 1}
          serviceType={template.service_type}
          onPickItem={addPickedItem}
          onAddCustom={addCustomLine}
        />
      )}
    </div>
  );
}
